/**
 * Stage 73.20u — Legacy / Historical Payroll Import — Admin landing.
 *
 * Admins use this page when going live mid-financial-year:
 *   1. Create an Import envelope for a specific (company, year, month).
 *   2. Upload the actual paid payroll CSV (one row per employee, all heads).
 *   3. Click "Compute Variances" — system runs the current correct engine
 *      against the uploaded actuals and stores per-head differences.
 *   4. Open the drill-down to action each variance (auto-adjust / recover /
 *      refund / employer-absorb / escalate) + approve.
 *   5. Finalize — creates a payroll_runs envelope tagged `legacy_import`
 *      so the historical month appears in Salary Register listings with
 *      an admin-only "Historical Import" banner.
 *
 * Admin-only — banner deliberately NOT shown to employees on payslips
 * to avoid confusing them. See `routes_legacy_payroll.finalize_import`.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import MainLayout from '../components/MainLayout';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import {
  Loader2, Plus, Upload, FileSpreadsheet, Download,
  AlertTriangle, CheckCircle2, ArrowRight, X, History,
  CircleDashed, Trash2, RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useActiveCompany } from '../hooks/useActiveCompany';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const STATUS_META = {
  draft: { label: 'Draft', tone: 'bg-slate-100 text-slate-700 border-slate-300' },
  uploaded: { label: 'Uploaded', tone: 'bg-sky-50 text-sky-700 border-sky-300' },
  variances_computed: { label: 'Variances Computed', tone: 'bg-amber-50 text-amber-800 border-amber-300' },
  finalized: { label: 'Finalized', tone: 'bg-emerald-50 text-emerald-800 border-emerald-300' },
};

export default function LegacyPayrollImports() {
  const [selectedCompany] = useActiveCompany();
  const [imports, setImports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    statutory_year: new Date().getFullYear(),
    statutory_month: new Date().getMonth() + 1,
    name: '',
    remarks: '',
    final_payroll_source: 'legacy',
  });
  const [conflictWarning, setConflictWarning] = useState(null);

  const loadAll = useCallback(async () => {
    if (!selectedCompany) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/legacy-payroll/imports?company_id=${selectedCompany}`, { withCredentials: true });
      // Stage 73.20ce — defensive: always normalise to an array.
      setImports(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      setImports([]);
      toast.error('Load failed: ' + (e?.response?.data?.detail || e.message));
    }
    setLoading(false);
  }, [selectedCompany]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Stage 73.20cd — conflict check whenever the user changes year/month in the
  // create modal.
  useEffect(() => {
    if (!showCreate || !selectedCompany) { setConflictWarning(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/legacy-payroll/conflict-check`, {
          withCredentials: true,
          params: {
            company_id: selectedCompany,
            statutory_year: Number(form.statutory_year),
            statutory_month: Number(form.statutory_month),
          },
        });
        if (!cancelled && r.data?.warning) setConflictWarning(r.data);
        else if (!cancelled) setConflictWarning(null);
      } catch {
        if (!cancelled) setConflictWarning(null);
      }
    })();
    return () => { cancelled = true; };
  }, [showCreate, selectedCompany, form.statutory_year, form.statutory_month]);


  const submitCreate = async () => {
    if (!selectedCompany) { toast.error('Select a company first'); return; }
    setCreating(true);
    try {
      const body = {
        company_id: selectedCompany,
        statutory_year: Number(form.statutory_year),
        statutory_month: Number(form.statutory_month),
        name: form.name.trim() || `Legacy Import — ${MONTHS[form.statutory_month - 1]} ${form.statutory_year}`,
        remarks: form.remarks.trim(),
        final_payroll_source: form.final_payroll_source || 'legacy',
      };
      await axios.post(`${API}/legacy-payroll/imports`, body, { withCredentials: true });
      toast.success('Import envelope created');
      setShowCreate(false);
      setForm({ statutory_year: new Date().getFullYear(), statutory_month: new Date().getMonth() + 1, name: '', remarks: '', final_payroll_source: 'legacy' });
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
    setCreating(false);
  };

  const stats = useMemo(() => {
    const list = Array.isArray(imports) ? imports : [];
    const counts = { total: list.length, draft: 0, uploaded: 0, variances_computed: 0, finalized: 0 };
    list.forEach(i => { counts[i.status] = (counts[i.status] || 0) + 1; });
    return counts;
  }, [imports]);

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="legacy-imports-page">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
              <History className="w-7 h-7 text-amber-600" />
              Legacy / Historical Payroll Import
            </h1>
            <p className="text-sm text-slate-600 mt-1 max-w-3xl">
              Use this when going live mid-financial-year. Upload actual paid payroll
              for past months as-is — the system stores them verbatim and computes
              variance against the current engine so you can recover / refund cleanly
              in the next normal run.
            </p>
          </div>
          <Button
            data-testid="create-legacy-import-btn"
            onClick={() => setShowCreate(true)}
            className="bg-amber-600 hover:bg-amber-700 text-white"
          >
            <Plus className="w-4 h-4 mr-2" /> New Legacy Import
          </Button>
        </div>

        {/* Workflow clarity — answers "do I also need to process the period in the engine?" */}
        <Card className="p-5 bg-emerald-50 border-emerald-200" data-testid="legacy-workflow-card">
          <h3 className="text-sm font-bold text-emerald-900 mb-2 flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" />
            Legacy vs Normal — what do I run first?
          </h3>
          <div className="grid md:grid-cols-2 gap-4 text-xs text-emerald-900">
            <div>
              <div className="font-semibold text-emerald-800 mb-1">PAST months (e.g. April + May 2026, paid outside the system)</div>
              <ol className="list-decimal list-inside space-y-1">
                <li>Legacy Import <strong>only</strong> — upload &rarr; compute variances &rarr; action &rarr; approve &rarr; finalize.</li>
                <li>Do <strong>NOT</strong> create a normal payroll period or run the engine for these months.</li>
                <li>The salary register for these months IS the uploaded file (verbatim).</li>
              </ol>
            </div>
            <div>
              <div className="font-semibold text-emerald-800 mb-1">FIRST live month (e.g. June 2026)</div>
              <ol className="list-decimal list-inside space-y-1">
                <li>Create a normal payroll period &rarr; <strong>Payroll Run</strong> &rarr; Process.</li>
                <li>Approved legacy adjustments auto-attach as <code className="bg-emerald-100 px-1 text-[10px]">LEGACY_REFUND_*</code> / <code className="bg-emerald-100 px-1 text-[10px]">LEGACY_RECOVERY_*</code> lines on affected employees.</li>
                <li>The engine refuses to re-process any finalized legacy_import run (HTTP 400) — historical actuals are immutable.</li>
              </ol>
            </div>
          </div>
        </Card>

        {/* Stats tiles */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Stat label="Total" value={stats.total} tone="slate" />
          <Stat label="Draft" value={stats.draft || 0} tone="slate" />
          <Stat label="Uploaded" value={stats.uploaded || 0} tone="sky" />
          <Stat label="Variances Computed" value={stats.variances_computed || 0} tone="amber" />
          <Stat label="Finalized" value={stats.finalized || 0} tone="emerald" />
        </div>

        {/* List card */}
        <Card className="p-0 overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-slate-800">Imports for this company</h2>
            <Button variant="outline" size="sm" onClick={loadAll} disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Refresh'}
            </Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left px-4 py-2">Period</th>
                  <th className="text-left px-4 py-2">Name</th>
                  <th className="text-left px-4 py-2">Status</th>
                  <th className="text-right px-4 py-2">Rows</th>
                  <th className="text-right px-4 py-2">Total Net</th>
                  <th className="text-right px-4 py-2">Variance Sum</th>
                  <th className="text-left px-4 py-2">Created</th>
                  <th className="text-right px-4 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {imports.length === 0 && (
                  <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-500">
                    No legacy imports yet. Click <strong>New Legacy Import</strong> to start.
                  </td></tr>
                )}
                {imports.map(imp => (
                  <ImportRow key={imp.id} imp={imp} onChanged={loadAll} />
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Helper */}
        <Card className="p-5 bg-blue-50 border-blue-200">
          <h3 className="text-sm font-semibold text-blue-900 mb-2">How it works</h3>
          <ol className="text-xs text-blue-800 space-y-1 list-decimal list-inside leading-relaxed">
            <li>Create one envelope per <strong>(company × month × year)</strong> for the historical period.</li>
            <li>Upload the CSV with one row per employee and all heads as columns
                (Fixed Gross, Earned Gross, Basic, HRA, PF, ESI, PT, TDS, Net Pay, etc.).</li>
            <li>Click <strong>Compute Variances</strong> — system reruns the current engine and stores the per-head delta.</li>
            <li>Open the <strong>Variances dashboard</strong>: pick an action per variance (recover / refund / adjust / employer-absorb / escalate) and approve.</li>
            <li><strong>Finalize</strong> the import. From the next normal payroll run, approved adjustments auto-create
                <code className="bg-blue-100 px-1 mx-1 rounded text-[10px]">LEGACY_REFUND_*</code> / <code className="bg-blue-100 px-1 mx-1 rounded text-[10px]">LEGACY_RECOVERY_*</code> lines on the employee's payslip.</li>
          </ol>
        </Card>
      </div>

      {/* Create modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="max-w-lg w-full p-6 bg-white">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-slate-900">New Legacy Import</h2>
              <button onClick={() => setShowCreate(false)} className="text-slate-400 hover:text-slate-700">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-3">
              <Field label="Statutory Month">
                <select
                  data-testid="legacy-import-month"
                  value={form.statutory_month}
                  onChange={e => setForm(f => ({ ...f, statutory_month: e.target.value }))}
                  className="w-full px-3 py-2 border rounded text-sm">
                  {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                </select>
              </Field>
              <Field label="Statutory Year">
                <input
                  data-testid="legacy-import-year"
                  type="number" min={2020} max={2099}
                  value={form.statutory_year}
                  onChange={e => setForm(f => ({ ...f, statutory_year: e.target.value }))}
                  className="w-full px-3 py-2 border rounded text-sm" />
              </Field>
              <Field label="Name (optional)">
                <input
                  data-testid="legacy-import-name"
                  type="text"
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder={`Legacy Import — ${MONTHS[form.statutory_month - 1]} ${form.statutory_year}`}
                  className="w-full px-3 py-2 border rounded text-sm" />
              </Field>
              <Field label="Remarks">
                <textarea
                  data-testid="legacy-import-remarks"
                  value={form.remarks}
                  rows={3}
                  onChange={e => setForm(f => ({ ...f, remarks: e.target.value }))}
                  className="w-full px-3 py-2 border rounded text-sm" />
              </Field>
              <Field label="Final Payroll Source">
                <select
                  data-testid="legacy-final-source"
                  value={form.final_payroll_source}
                  onChange={e => setForm(f => ({ ...f, final_payroll_source: e.target.value }))}
                  className="w-full px-3 py-2 border rounded text-sm">
                  <option value="legacy">Legacy (uploaded actuals override engine)</option>
                  <option value="regular">Regular (engine values stay authoritative)</option>
                </select>
                <p className="text-[11px] text-slate-500 mt-1">
                  Default <strong>Legacy</strong>: payslip + salary register strictly use uploaded
                  values. Switch to <strong>Regular</strong> if the legacy file is for variance
                  audit only.
                </p>
              </Field>
              {conflictWarning && (
                <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900"
                     data-testid="legacy-conflict-warning">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-700" />
                    <div>
                      <p className="font-semibold">Conflict detected</p>
                      <p className="mt-0.5">{conflictWarning.warning}</p>
                      {conflictWarning.regular_runs?.length > 0 && (
                        <p className="mt-1">
                          Regular runs found: <strong>{conflictWarning.regular_runs.length}</strong>
                          {' '}({conflictWarning.regular_runs.map(r => r.status).join(', ')})
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button
                data-testid="legacy-import-create-submit"
                onClick={submitCreate}
                disabled={creating}
                className="bg-amber-600 hover:bg-amber-700 text-white"
              >
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create Envelope'}
              </Button>
            </div>
          </Card>
        </div>
      )}
    </MainLayout>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-700 mb-1">{label}</label>
      {children}
    </div>
  );
}

function Stat({ label, value, tone = 'slate' }) {
  const toneMap = {
    slate: 'bg-slate-50 border-slate-200 text-slate-800',
    sky: 'bg-sky-50 border-sky-200 text-sky-800',
    amber: 'bg-amber-50 border-amber-200 text-amber-800',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
  };
  return (
    <div className={`rounded-lg border p-4 ${toneMap[tone]}`}>
      <div className="text-2xl font-bold">{value}</div>
      <div className="text-[11px] uppercase tracking-wide opacity-80 mt-1">{label}</div>
    </div>
  );
}

function ImportRow({ imp, onChanged }) {
  const meta = STATUS_META[imp.status] || STATUS_META.draft;
  const [uploading, setUploading] = useState(false);
  const [computing, setComputing] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [showColumns, setShowColumns] = useState(false);
  const [columns, setColumns] = useState(null);
  const fileInputRef = React.useRef(null);

  const handlePreviewColumns = async () => {
    if (!columns) {
      try {
        const r = await axios.get(`${API}/legacy-payroll/imports/${imp.id}/template-headers`, { withCredentials: true });
        setColumns(r.data);
      } catch (e) {
        toast.error('Could not load template columns');
        return;
      }
    }
    setShowColumns(s => !s);
  };

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(
        `${API}/legacy-payroll/imports/${imp.id}/upload`,
        fd,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } },
      );
      // Stage 73.20ce — defensive defaults on the response payload.
      const data = r.data || {};
      const rows_imported = Number(data.rows_imported || 0);
      const rows_failed = Number(data.rows_failed || 0);
      const unknown_columns = Array.isArray(data.unknown_columns) ? data.unknown_columns : [];
      if (rows_failed > 0) {
        toast.warning(`Imported ${rows_imported} rows • ${rows_failed} failed${unknown_columns.length ? ` • ${unknown_columns.length} unknown columns` : ''}`);
      } else {
        toast.success(`Uploaded ${rows_imported} rows`);
      }
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
    setUploading(false);
  };

  const handleCompute = async () => {
    setComputing(true);
    try {
      const r = await axios.post(`${API}/legacy-payroll/imports/${imp.id}/compute-variances`, {}, { withCredentials: true });
      const total = r.data?.rows_with_variance || 0;
      toast.success(`Variance run complete — ${total} differences flagged`);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
    setComputing(false);
  };

  const handleFinalize = async () => {
    if (!window.confirm('Finalize this import? After this, no more uploads or re-computation. The historical month will show up in the Salary Register with an admin-only "Historical Import" banner.')) return;
    setFinalizing(true);
    try {
      await axios.post(`${API}/legacy-payroll/imports/${imp.id}/finalize`, {}, { withCredentials: true });
      toast.success('Import finalized — historical run created');
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
    setFinalizing(false);
  };

  const handleTemplate = async () => {
    try {
      const r = await axios.get(`${API}/legacy-payroll/imports/${imp.id}/template.csv`, { withCredentials: true, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `legacy_payroll_template_${imp.statutory_year}_${imp.statutory_month}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error('Template download failed'); }
  };

  // Stage 73.20cd — Toggle Final Payroll Source + regenerate payslips + report
  const [togglingSrc, setTogglingSrc] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleToggleSource = async (newSrc) => {
    if (newSrc === (imp.final_payroll_source || 'legacy')) return;
    if (!window.confirm(
      `Switch Final Payroll Source to '${newSrc}'?\n\n` +
      (newSrc === 'legacy'
        ? 'Payslips & Salary Register will strictly use uploaded actuals after Regenerate.'
        : 'Payslips & Salary Register will use the engine-computed values from the Regular run.')
    )) return;
    setTogglingSrc(true);
    try {
      await axios.patch(`${API}/legacy-payroll/imports/${imp.id}/final-source`,
        { final_payroll_source: newSrc }, { withCredentials: true });
      toast.success(`Final Source set to '${newSrc}'. Click 'Regenerate' to refresh.`);
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setTogglingSrc(false);
  };

  const handleRegenerate = async () => {
    if (!window.confirm(`Regenerate legacy payroll data for ${period}? This will recalculate statutory engine values, salary register entries, payslips, and tax records cleanly for this import.`)) return;
    setRegenerating(true);
    try {
      const r = await axios.post(`${API}/legacy-payroll/imports/${imp.id}/regenerate`,
        {}, { withCredentials: true });
      toast.success(r.data?.message || `Regenerated ${r.data?.regenerated || r.data?.regenerated_count || 0} records for ${period}.`);
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setRegenerating(false);
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/legacy-payroll/imports/${imp.id}`, { withCredentials: true });
      toast.success(`Deleted legacy import for ${period}`);
      setShowDeleteConfirm(false);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
    setDeleting(false);
  };

  const handleValidationReport = async () => {
    try {
      const r = await axios.get(`${API}/legacy-payroll/imports/${imp.id}/validation-report.csv`,
        { withCredentials: true, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `legacy_validation_${imp.statutory_year}_${imp.statutory_month}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error('Validation report download failed'); }
  };

  // Stage 73.20ce — Backfill payslips + payroll_run_details from legacy
  // lines (for pre-stage finalized imports that lack materialised rows).
  const [backfilling, setBackfilling] = useState(false);
  const [debugSummary, setDebugSummary] = useState(null);
  const handleBackfill = async () => {
    if (!window.confirm('Backfill payslips & salary register rows from the uploaded legacy lines? Safe to run multiple times.')) return;
    setBackfilling(true);
    try {
      const r = await axios.post(`${API}/legacy-payroll/imports/${imp.id}/backfill-from-legacy`,
        {}, { withCredentials: true });
      const data = r.data || {};
      toast.success(`Backfilled ${data.payslip_rows || 0} payslips & ${data.run_detail_rows || 0} register rows.`);
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setBackfilling(false);
  };
  const handleDebug = async () => {
    try {
      const r = await axios.get(`${API}/legacy-payroll/imports/${imp.id}/debug-summary`,
        { withCredentials: true });
      setDebugSummary(r.data || null);
    } catch (e) { toast.error('Debug failed: ' + (e?.response?.data?.detail || e.message)); }
  };

  const totalNet = imp.totals?.net_pay || 0;
  const varianceSum = useMemo(() => {
    if (!imp.variance_summary) return 0;
    return Object.values(imp.variance_summary).reduce((sum, v) => sum + Math.abs(v?.diff_sum || 0), 0);
  }, [imp.variance_summary]);

  const period = `${MONTHS[(imp.statutory_month || 1) - 1]} ${imp.statutory_year}`;

  return (
    <>
    <tr className="border-b border-slate-100 hover:bg-slate-50" data-testid={`legacy-import-row-${imp.id}`}>
      <td className="px-4 py-3 font-medium text-slate-800">{period}</td>
      <td className="px-4 py-3 text-slate-700">{imp.name}</td>
      <td className="px-4 py-3">
        <span className={`text-[11px] font-medium px-2 py-1 rounded border ${meta.tone}`}>{meta.label}</span>
      </td>
      <td className="px-4 py-3 text-right">
        {imp.rows_imported || 0}
        {imp.rows_failed > 0 && (
          <span className="ml-1 text-rose-600 text-xs">/ {imp.rows_failed} failed</span>
        )}
      </td>
      <td className="px-4 py-3 text-right tabular-nums">₹{(totalNet || 0).toLocaleString('en-IN')}</td>
      <td className="px-4 py-3 text-right tabular-nums">
        {imp.status === 'variances_computed' || imp.status === 'finalized' ? (
          <span className={varianceSum > 0 ? 'text-amber-700 font-semibold' : 'text-slate-500'}>
            ₹{varianceSum.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
          </span>
        ) : <span className="text-slate-400">—</span>}
      </td>
      <td className="px-4 py-3 text-xs text-slate-500">
        {imp.created_at ? new Date(imp.created_at).toLocaleDateString() : '—'}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center justify-end gap-1 flex-wrap">
          <Button size="sm" variant="ghost" onClick={handleTemplate} title="Download CSV template">
            <Download className="w-3.5 h-3.5" />
          </Button>
          <Button
            size="sm" variant="ghost"
            onClick={handlePreviewColumns}
            title="Preview expected columns based on your salary heads"
            data-testid={`legacy-preview-columns-btn-${imp.id}`}
          >
            <FileSpreadsheet className="w-3.5 h-3.5" />
          </Button>
          {imp.status !== 'finalized' && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,.xls"
                className="hidden"
                onChange={(e) => { handleUpload(e.target.files[0]); e.target.value = ''; }}
                data-testid={`legacy-import-upload-input-${imp.id}`}
              />
              <Button
                size="sm" variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                data-testid={`legacy-import-upload-btn-${imp.id}`}
              >
                {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1" />}
                {imp.status === 'draft' ? 'Upload CSV' : 'Re-upload'}
              </Button>
            </>
          )}
          {imp.status === 'uploaded' && (
            <Button
              size="sm"
              onClick={handleCompute}
              disabled={computing}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid={`legacy-compute-variances-btn-${imp.id}`}
            >
              {computing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CircleDashed className="w-3.5 h-3.5 mr-1" />}
              Compute Variances
            </Button>
          )}
          {(imp.status === 'variances_computed' || imp.status === 'finalized') && (
            <Link to={`/payroll/legacy-imports/${imp.id}`}>
              <Button size="sm" variant="outline" data-testid={`legacy-open-variances-btn-${imp.id}`}>
                <ArrowRight className="w-3.5 h-3.5 mr-1" /> Variances
              </Button>
            </Link>
          )}
          {imp.status === 'variances_computed' && (
            <Button
              size="sm"
              onClick={handleFinalize}
              disabled={finalizing}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid={`legacy-finalize-btn-${imp.id}`}
            >
              {finalizing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1" />}
              Finalize
            </Button>
          )}
          {imp.status !== 'draft' && (
            <Button
              size="sm" variant="outline"
              onClick={handleRegenerate}
              disabled={regenerating}
              data-testid={`legacy-regenerate-btn-${imp.id}`}
              title="Regenerate payslips & register from current Final Source"
            >
              {regenerating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Regenerate'}
            </Button>
          )}
          {imp.status === 'finalized' && (
            <>
              <select
                data-testid={`legacy-final-source-toggle-${imp.id}`}
                value={imp.final_payroll_source || 'legacy'}
                onChange={(e) => handleToggleSource(e.target.value)}
                disabled={togglingSrc}
                className="text-[11px] border-2 border-slate-300 rounded px-1.5 py-1 bg-white"
                title="Final Payroll Source"
              >
                <option value="legacy">Source: Legacy</option>
                <option value="regular">Source: Regular</option>
              </select>
              <Button
                size="sm" variant="ghost"
                onClick={handleValidationReport}
                data-testid={`legacy-validation-report-btn-${imp.id}`}
                title="Download Legacy Validation Report"
              >
                <Download className="w-3.5 h-3.5" />
              </Button>
              <Button
                size="sm" variant="ghost"
                onClick={handleBackfill}
                disabled={backfilling}
                data-testid={`legacy-backfill-btn-${imp.id}`}
                title="Backfill payslips + register from legacy lines (one-click fix for pre-Stage 73.20cd imports)"
              >
                {backfilling ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Backfill'}
              </Button>
              <Button
                size="sm" variant="ghost"
                onClick={handleDebug}
                data-testid={`legacy-debug-btn-${imp.id}`}
                title="Show diagnostic summary for this import"
              >
                Debug
              </Button>
            </>
          )}
          <Button
            size="sm" variant="ghost"
            onClick={() => setShowDeleteConfirm(true)}
            disabled={deleting}
            className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
            title="Delete this legacy import"
            data-testid={`legacy-delete-btn-${imp.id}`}
          >
            {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5 text-rose-600" />}
          </Button>
        </div>

        {/* Delete confirmation modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <Card className="max-w-md w-full p-6 bg-white space-y-4">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-rose-100 text-rose-700 rounded-full shrink-0 mt-0.5">
                  <Trash2 className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-slate-900">Delete Legacy Import?</h3>
                  <p className="text-xs text-slate-600 mt-1">
                    Are you sure you want to delete legacy import <strong>"{imp.name}"</strong> for <strong>{period}</strong>?
                  </p>
                  <div className="mt-3 p-3 bg-rose-50 border border-rose-200 rounded text-[11px] text-rose-800 space-y-1">
                    <p className="font-semibold">• All uploaded source lines for this import will be deleted.</p>
                    <p className="font-semibold">• Legacy salary register details, payslips, and tax records for this import will be removed.</p>
                    <p className="font-semibold">• Employee Master data and CTC structures will NOT be deleted.</p>
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" size="sm" onClick={() => setShowDeleteConfirm(false)} disabled={deleting}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleDelete}
                  disabled={deleting}
                  className="bg-rose-600 hover:bg-rose-700 text-white"
                  data-testid={`legacy-confirm-delete-btn-${imp.id}`}
                >
                  {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Trash2 className="w-4 h-4 mr-1" />}
                  Delete Import
                </Button>
              </div>
            </Card>
          </div>
        )}
        {/* Debug summary panel — appears under the row when 'Debug' clicked. */}
        {debugSummary && imp.status === 'finalized' && (
          <div className="mt-2 rounded-lg border border-slate-300 bg-slate-50 p-3 text-[11px] font-mono space-y-1"
               data-testid={`legacy-debug-panel-${imp.id}`}>
            <div className="flex items-center justify-between mb-1">
              <span className="font-semibold text-slate-700">DEBUG · {debugSummary.payroll_month}</span>
              <button onClick={() => setDebugSummary(null)} className="text-slate-500 hover:text-slate-700">
                <X className="w-3 h-3" />
              </button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-x-3 gap-y-0.5">
              <span>upload_status: <strong>{debugSummary.upload_status}</strong></span>
              <span>freeze_status: <strong>{debugSummary.freeze_status}</strong></span>
              <span>final_source: <strong>{debugSummary.final_payroll_source}</strong></span>
              <span>uploaded_rows: <strong>{debugSummary.total_uploaded_rows}</strong></span>
              <span>saved_rows: <strong>{debugSummary.total_saved_rows}</strong></span>
              <span>error_rows: <strong>{debugSummary.total_error_rows}</strong></span>
              <span>variance_rows: <strong>{debugSummary.variance_rows}</strong></span>
              <span>payslip_rows: <strong className={debugSummary.payslip_rows === 0 ? 'text-rose-700' : 'text-emerald-700'}>{debugSummary.payslip_rows}</strong></span>
              <span>register_rows: <strong className={debugSummary.run_detail_rows === 0 ? 'text-rose-700' : 'text-emerald-700'}>{debugSummary.run_detail_rows}</strong></span>
              <span>gross_total: <strong>₹{Number(debugSummary.gross_total || 0).toLocaleString('en-IN')}</strong></span>
              <span>net_pay_total: <strong>₹{Number(debugSummary.net_pay_total || 0).toLocaleString('en-IN')}</strong></span>
              <span>pf_total: <strong>₹{Number(debugSummary.pf_total || 0).toLocaleString('en-IN')}</strong></span>
              <span>esi_total: <strong>₹{Number(debugSummary.esi_total || 0).toLocaleString('en-IN')}</strong></span>
              <span>pt_total: <strong>₹{Number(debugSummary.pt_total || 0).toLocaleString('en-IN')}</strong></span>
              <span>tds_total: <strong>₹{Number(debugSummary.tds_total || 0).toLocaleString('en-IN')}</strong></span>
            </div>
            {Array.isArray(debugSummary.messages) && debugSummary.messages.length > 0 && (
              <div className="mt-1 text-amber-700">
                {debugSummary.messages.map((m, i) => <div key={i}>• {m}</div>)}
              </div>
            )}
          </div>
        )}
        {Array.isArray(imp.row_errors) && imp.row_errors.length > 0 && imp.status === 'uploaded' && (
          <div className="mt-2 text-[10px] text-rose-600 flex items-start gap-1">
            <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
            <span>{imp.row_errors.length} row error{imp.row_errors.length > 1 ? 's' : ''} — open Variances to view detail</span>
          </div>
        )}
      </td>
    </tr>
    {showColumns && columns && (() => {
      // Stage 73.20ce — defensive array normalisation to prevent
      // `Cannot read properties of undefined (reading 'length')`.
      const dyn = Array.isArray(columns.dynamic_columns) ? columns.dynamic_columns : [];
      const stat = Array.isArray(columns.statutory_columns) ? columns.statutory_columns : [];
      return (
      <tr className="bg-amber-50/40 border-b border-amber-200" data-testid={`legacy-columns-panel-${imp.id}`}>
        <td colSpan={8} className="px-6 py-4">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div>
              <h4 className="text-xs font-bold text-amber-900 uppercase tracking-wide">
                Expected CSV columns for this company ({dyn.length + stat.length} total)
              </h4>
              <p className="text-xs text-slate-600 mt-1">
                Template auto-builds from your Salary Heads master + statutory baseline. Upload accepts any of these column names (case-insensitive).
              </p>
            </div>
            <button onClick={() => setShowColumns(false)} className="text-amber-700 hover:text-amber-900">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1">
                Statutory / meta (always present)
              </div>
              <div className="flex flex-wrap gap-1">
                {stat.map(c => (
                  <span key={c.key}
                    className={`text-[11px] px-2 py-0.5 rounded border ${c.required ? 'bg-rose-50 border-rose-300 text-rose-700' : 'bg-slate-50 border-slate-300 text-slate-700'}`}>
                    {c.label}{c.required ? ' *' : ''}
                  </span>
                ))}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1">
                Dynamic — from your Salary Heads master
              </div>
              {dyn.length === 0 ? (
                <div className="text-xs text-slate-500 italic">
                  No additional company-specific heads defined. The statutory baseline above is sufficient.
                </div>
              ) : (
                <div className="flex flex-wrap gap-1">
                  {dyn.map(c => (
                    <span key={c.code}
                      className={`text-[11px] px-2 py-0.5 rounded border ${c.head_type === 'earning' ? 'bg-emerald-50 border-emerald-300 text-emerald-700' : 'bg-amber-50 border-amber-300 text-amber-800'}`}
                      title={`${c.code} — ${c.head_type}`}>
                      {c.name} ({c.head_type === 'earning' ? 'E' : 'D'})
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="mt-3 text-[10px] text-slate-500">
            <span className="text-rose-700 font-semibold">Red *</span> = required • <span className="text-emerald-700 font-semibold">Green</span> = earning • <span className="text-amber-700 font-semibold">Amber</span> = deduction. Add or rename heads in <strong>Masters → Salary Heads</strong>, then re-download the template.
          </div>
        </td>
      </tr>
      );
    })()}
    </>
  );
}
